import _ from 'lodash'
import React, { useEffect, useState } from 'react'

import { NEUTRAL_COLOURS } from 'constants/colors'
import { FormBuilderField, FormTypes } from 'services/formBuilderSettings/models'
import { NurserySettingsFormBuilder } from 'services/nurseries/models'

import { Checkbox, Flex, Grid, Icon, Space, Spinner, Switch, Tooltip, Typography } from 'components'

import i18n from 'translations'

import Agreement from '../Agreement'
import NumberOfParents from '../NumberOfParents'
import TermsAndConditions from '../TermsAndConditions'
import { StyledFieldItem } from './FieldStyled'

interface FieldProps {
  field: FormBuilderField
  isFetching: boolean
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
  onChange: (value: {
    field: FormBuilderField
    formType?: FormTypes
    value: any
  }) => void
}

const Field = ({ field, isFetching, nurseryFormBuilderSettings, onChange }: FieldProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialised, setIsInitialised] = useState(false)
  const [value, setValue] = useState<boolean | string | number>(true)

  const getValue = (data: any, keys: string[]) => {
    if (1 === keys.length) {
      if (true === data[keys[0]]) {
        return false
      }

      if (_.isString(data[keys[0]])) {
        return data[keys[0]]
      }
    }

    if (1 < keys.length && _.isObject(data[keys[0]])) {
      const reducedKeys = keys.slice(1)

      return getValue(data[keys[0]], reducedKeys)
    }

    return true
  }

  useEffect(() => {
    if (field && nurseryFormBuilderSettings) {
      const keys = _.split(field.key, '.')

      if (1 === keys.length) {
        setValue(nurseryFormBuilderSettings[keys[0]])
      } else {
        setValue(getValue(nurseryFormBuilderSettings, keys))
      }

      setIsInitialised(true)
    }
  }, [field, nurseryFormBuilderSettings])

  const handleChange = async (newValue: any) => {
    setIsLoading(true)
    setValue(newValue)

    await onChange({
      field,
      value: newValue,
    })

    setIsLoading(false)
  }

  if ('termsAndConditions' === field.key) {
    return (
      <React.Fragment>
        <Typography padding="8px 0" bold>
          {i18n.t('module:Management:FormBuilder:fields:termsAndConditions:label')}
        </Typography>
        <Typography fontSize={14} lineHeight={20} padding="5px 0">
          {i18n.t('module:Management:FormBuilder:fields:termsAndConditions:description1')}
          <br />
          <br />
          <Typography fontSize={14} bold>
            {i18n.t('module:Management:FormBuilder:fields:termsAndConditions:description2')}
          </Typography>
          {i18n.t('module:Management:FormBuilder:fields:termsAndConditions:description3')}
          <br />
          <br />
          <Typography fontSize={14} bold>
            {i18n.t('module:Management:FormBuilder:fields:termsAndConditions:description4')}
          </Typography>
          {i18n.t('module:Management:FormBuilder:fields:termsAndConditions:description5')}
          <br />
          <br />
          {i18n.t('module:Management:FormBuilder:fields:termsAndConditions:description6')}
        </Typography>
        <Space space="20px" />
        <StyledFieldItem>
          {isInitialised
            ? (
              <TermsAndConditions
                disabled={isLoading}
                initialValues={{ value: value as string }}
                nurseryFormBuilderSettings={nurseryFormBuilderSettings}
                onSubmit={handleChange}
              />
            )
            : <Spinner />}
        </StyledFieldItem>
      </React.Fragment>
    )
  }

  if ('agreement' === field.key) {
    return (
      <React.Fragment>
        <Typography padding="8px 0" bold>
          {i18n.t('module:Management:FormBuilder:fields:agreement:label')}
        </Typography>
        <Typography fontSize={14} padding="5px 0">
          {i18n.t('module:Management:FormBuilder:fields:agreement:description')}
        </Typography>
        <Space space="20px" />
        <StyledFieldItem>
          {isInitialised
            ? (
              <Agreement disabled={isLoading} initialValues={{ value: value as string }} onSubmit={handleChange} />
            )
            : <Spinner />}
        </StyledFieldItem>
      </React.Fragment>
    )
  }

  if ('includeBookingPattern' === field.key) {
    return (
      <StyledFieldItem key={field.key}>
        <Grid spacing={8}>
          <Grid.Item>
            <Checkbox disabled={isLoading} value={value as boolean} onChange={handleChange} />
          </Grid.Item>
          <Grid.Item flex={{ desktop: 1 }}>
            <Flex flexDirection="column">
              <Typography margin="0 0 2px" onClick={() => !isLoading && handleChange(!value)}>
                {i18n.t('module:Management:FormBuilder:fields:bookingPattern:label')}
              </Typography>
              <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14}>
                {i18n.t('module:Management:FormBuilder:fields:bookingPattern:tooltip')}
              </Typography>
            </Flex>
          </Grid.Item>
        </Grid>
      </StyledFieldItem>
    )
  }

  if ('enrollmentDetails' === field.key) {
    return (
      <StyledFieldItem key={field.key}>
        <Grid spacing={8}>
          <Grid.Item>
            <Checkbox disabled={isLoading} value={value as boolean} onChange={handleChange} />
          </Grid.Item>
          <Grid.Item flex={{ desktop: 1 }}>
            <Flex flexDirection="column">
              <Typography margin="0 0 2px" onClick={() => !isLoading && handleChange(!value)}>
                {i18n.t('module:Management:FormBuilder:fields:enrollmentDetails:label')}
              </Typography>
              <Typography color={NEUTRAL_COLOURS.GRAY} fontSize={14}>
                {i18n.t('module:Management:FormBuilder:fields:enrollmentDetails:tooltip')}
              </Typography>
            </Flex>
          </Grid.Item>
        </Grid>
      </StyledFieldItem>
    )
  }

  if ('maxParents' === field.key) {
    return (
      <StyledFieldItem key={field.key}>
        <Grid spacing={8}>
          <Grid.Item alignItems={{ desktop: 'center' }}>
            <Icon color={NEUTRAL_COLOURS.BASIC} height={18} icon="contact" />
          </Grid.Item>
          <Grid.Item alignItems={{ desktop: 'center' }} flex={{ desktop: 1 }}>
            <Typography>
              {i18n.t('module:Management:FormBuilder:fields:numberOfParents:label')}
            </Typography>
          </Grid.Item>
          <Grid.Item alignItems={{ desktop: 'center' }}>
            <NumberOfParents initialValue={value as number} isLoading={isLoading} onChange={handleChange} />
          </Grid.Item>
        </Grid>
      </StyledFieldItem>
    )
  }

  return (
    <StyledFieldItem key={field.key}>
      <Grid spacing={0}>
        <Grid.Item alignItems={{ desktop: 'center' }} flex={{ desktop: 1 }}>
          <Typography>
            {field.label}
          </Typography>
        </Grid.Item>
        {/* TODO: V2 */}
        {/* <Grid.Item
          alignItems={{ desktop: 'center' }}
          justifyContent={{ desktop: 'flex-end' }}
          width={{ desktop: '80px' }}
        >
          <Tooltip
            title={field.defaultDisabled?.enquiry
              ? i18n.t('module:Management:FormBuilder:thisFieldIsMandatory')
              : null}
            width={170}
          >
            <Switch
              disabled={field.defaultDisabled?.enquiry || isLoading}
              value={false}
              onChange={handleChange(FormTypes.ENQUIRY)}
            />
          </Tooltip>
        </Grid.Item> */}
        <Grid.Item
          alignItems={{ desktop: 'center' }}
          justifyContent={{ desktop: 'flex-end' }}
          width={{ desktop: '150px' }}
        >
          <Flex gap="5px">
            <Flex alignItems="center" justifyContent="center">
              {isLoading && <Spinner size="small" />}
            </Flex>
            <Flex>
              <Tooltip
                title={field.defaultDisabled?.registration
                  ? i18n.t('module:Management:FormBuilder:thisFieldIsMandatory')
                  : null}
                width={170}
              >
                <Switch
                  disabled={field.defaultDisabled?.registration || isFetching || isLoading}
                  value={value as boolean}
                  onChange={handleChange}
                />
              </Tooltip>
            </Flex>
          </Flex>
        </Grid.Item>
      </Grid>
    </StyledFieldItem>
  )
}

export default Field
